import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import {
  countActiveAdvancedFilters,
  defaultIndividualPaymentFilters,
  hasAnyIndividualPaymentFilter,
  individualPaymentAppliedFiltersAtom,
  individualPaymentFiltersAtom,
} from '../store/atoms/individualPaymentFiltersAtom';
import { ADVANCED_FILTER_KEYS, type IndividualPaymentFilters } from '../types/individualPayment';

/**
 * Gerenciamento dos filtros da pesquisa de Pagamento Individual.
 *
 * - `filters`: rascunho atualizado em tempo real pelos inputs.
 * - `updateFilters`: aplica patch parcial ao rascunho.
 * - `applyFilters`: promove o rascunho a "aplicado" — só aqui a busca dispara.
 * - `resetFilters`: limpa rascunho e aplicado, devolvendo a tela ao estado inicial.
 * - `resetAdvancedFilters`: limpa apenas o painel avançado, preservando os lookups.
 * - `canApply`: há ao menos um filtro preenchido no rascunho.
 * - `advancedActiveCount`: quantos filtros avançados estão preenchidos.
 *
 * Único ponto de acesso aos átomos de filtro — componentes nunca usam `useAtom`
 * diretamente.
 *
 * @example
 * ```tsx
 * const { filters, updateFilters, applyFilters, canApply } = useIndividualPaymentFilters();
 * ```
 */
export function useIndividualPaymentFilters() {
  const [filters, setFilters] = useAtom(individualPaymentFiltersAtom);
  const setApplied = useSetAtom(individualPaymentAppliedFiltersAtom);

  const updateFilters = useCallback(
    (partial: Partial<IndividualPaymentFilters>) => {
      setFilters(prev => ({ ...prev, ...partial }));
    },
    [setFilters],
  );

  const applyFilters = useCallback(() => {
    setApplied(filters);
  }, [filters, setApplied]);

  const resetFilters = useCallback(() => {
    setFilters(defaultIndividualPaymentFilters);
    setApplied(null);
  }, [setApplied, setFilters]);

  const resetAdvancedFilters = useCallback(() => {
    setFilters(prev => {
      const cleared = Object.fromEntries(ADVANCED_FILTER_KEYS.map(key => [key, '']));
      return { ...prev, ...cleared };
    });
  }, [setFilters]);

  return {
    filters,
    updateFilters,
    applyFilters,
    resetFilters,
    resetAdvancedFilters,
    canApply: hasAnyIndividualPaymentFilter(filters),
    advancedActiveCount: countActiveAdvancedFilters(filters),
  } as const;
}
